import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Bookmark, BookmarkCheck, Search, Share2 } from "lucide-react";
import { toast } from "sonner";
import { CONTENT_TYPE_LABELS, cachedGet, favorites, history } from "@/lib/publicApi";
import { EmergencyBar, Skeleton } from "./SaudeLayout";
import { Markdown } from "@/components/Markdown";
import { ContentImage } from "@/components/ContentImage";
import { Pill, StateBlock } from "@/components/common";
import { Input } from "@/components/ui/input";

const TYPES = [
  ["", "Tudo"],
  ["article", "Artigos"],
  ["guide", "Guias"],
  ["faq", "Perguntas frequentes"],
];

/** Temas do conteúdo, resolvidos pelo nome oficial da categoria. */
export const CategoryTags = ({ items, categories, slug }) => {
  const names = (items || [])
    .map((s) => (categories || []).find((c) => c.slug === s)?.name || null)
    .filter(Boolean);
  if (names.length === 0) return null;
  return (
    <span data-testid={`content-categories-${slug}`} className="mt-3 flex flex-wrap gap-1.5">
      {names.map((name) => (
        <span key={name} className="rounded-full bg-sand px-2.5 py-1 text-[11px] font-medium text-sage-700">
          {name}
        </span>
      ))}
    </span>
  );
};

export const ContentLibrary = () => {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [term, setTerm] = useState(params.get("q") || "");

  const type = params.get("type") || "";
  const category = params.get("category") || "";
  const q = params.get("q") || "";

  useEffect(() => {
    (async () => {
      try {
        const { data: cats } = await cachedGet("/categories");
        setCategories(cats);
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  useEffect(() => {
    setData(null);
    (async () => {
      try {
        const { data: payload } = await cachedGet("/contents", { type, category, q, page: 1, page_size: 20 });
        setData(payload);
      } catch {
        setData({ items: [], total: 0 });
      }
    })();
  }, [type, category, q]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <div data-testid="saude-contents-page" className="space-y-6">
      <div>
        <p className="er-label">Biblioteca</p>
        <h1 className="mt-3 font-display text-3xl font-medium tracking-tight text-ink-900">Conteúdos</h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setParam("q", term.trim());
        }}
        className="relative"
      >
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-400" />
        <Input
          data-testid="contents-search-input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Buscar por tema, dúvida ou palavra"
          aria-label="Buscar conteúdos"
          className="h-12 rounded-full bg-white pl-11"
        />
      </form>

      <div className="flex flex-wrap gap-2">
        {TYPES.map(([value, label]) => (
          <button
            key={label}
            data-testid={`contents-type-${value || "all"}`}
            onClick={() => setParam("type", value)}
            className={`min-h-[40px] rounded-full px-4 text-xs font-semibold transition-colors ${
              type === value ? "bg-sage-600 text-bone" : "bg-sand text-ink-600"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button
          data-testid="contents-category-all"
          onClick={() => setParam("category", "")}
          className={`min-h-[36px] shrink-0 whitespace-nowrap rounded-full px-4 text-xs transition-colors ${
            !category ? "bg-sage-600 text-white" : "bg-white text-ink-500 border border-border"
          }`}
        >
          Todos os temas
        </button>
        {categories.map((c) => (
          <button
            key={c.slug}
            data-testid={`contents-category-${c.slug}`}
            onClick={() => setParam("category", c.slug)}
            className={`min-h-[36px] shrink-0 whitespace-nowrap rounded-full px-4 text-xs transition-colors ${
              category === c.slug ? "bg-sage-600 text-white" : "bg-white text-ink-500 border border-border"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {!data ? (
        <Skeleton lines={4} testId="contents-skeleton" />
      ) : data.items.length === 0 ? (
        <StateBlock
          testId="contents-empty"
          title="Nada encontrado por aqui"
          description="Tente outra palavra ou remova os filtros para ver toda a biblioteca."
        />
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-ink-400">{data.total} conteúdo(s)</p>
          {data.items.map((c) => (
            <Link key={c.id} to={`/saude/conteudos/${c.slug}`} data-testid={`content-card-${c.slug}`} className="er-card block">
              <ContentImage src={c.image_url} alt={c.image_alt || c.title} testId={`content-card-image-${c.slug}`} className="mb-3" />
              <div className="flex items-center justify-between gap-2">
                <Pill>{CONTENT_TYPE_LABELS[c.type] || c.type}</Pill>
                <span className="text-xs text-ink-400">{c.reading_minutes} min</span>
              </div>
              <span className="mt-3 block font-display text-base font-medium text-ink-900">{c.title}</span>
              <span className="mt-1 block text-sm text-ink-500">{c.summary}</span>
              <CategoryTags items={c.categories} categories={categories} slug={c.slug} />
            </Link>
          ))}
        </div>
      )}
      <EmergencyBar />
    </div>
  );
};

export const ContentDetail = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: cats } = await cachedGet("/categories");
        setCategories(cats);
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: payload } = await cachedGet(`/contents/${slug}`);
        setData(payload);
        setSaved(favorites.has(slug));
        history.push({ slug, title: payload.content.title, type: payload.content.type });
      } catch {
        setError("Não foi possível carregar este conteúdo agora.");
      }
    })();
  }, [slug]);

  if (error) return <StateBlock testId="content-error" title="Conteúdo indisponível" description={error} />;
  if (!data) return <Skeleton lines={6} testId="content-detail-skeleton" />;

  const c = data.content;

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: c.title, text: `${c.title} — Erimus` });
      else {
        await navigator.clipboard.writeText(`${c.title} — Erimus – Saúde Mental · ${window.location.href}`);
        toast.success("Link copiado");
      }
    } catch {
      /* cancelado */
    }
  };

  return (
    <div data-testid="content-detail-page" className="space-y-6">
      <Link to="/saude/conteudos" data-testid="content-back" className="inline-flex items-center gap-2 text-sm text-ink-500">
        <ArrowLeft size={16} /> Conteúdos
      </Link>

      <div>
        <ContentImage
          src={c.image_url}
          alt={c.image_alt || c.title}
          testId={`content-detail-image-${slug}`}
          className="mb-4"
        />
        <Pill>{CONTENT_TYPE_LABELS[c.type] || c.type}</Pill>
        <h1 className="mt-3 font-display text-2xl font-medium tracking-tight text-ink-900">{c.title}</h1>
        <p className="mt-2 text-xs text-ink-400">
          {c.reading_minutes} min de leitura ·{" "}
          {c.published_at ? `publicado em ${new Date(c.published_at).toLocaleDateString("pt-BR")}` : "sem data"}
          {c.author ? ` · ${c.author}` : ""}
        </p>
        <CategoryTags items={c.categories} categories={categories} slug={slug} />
      </div>

      <div className="flex gap-3">
        <button
          data-testid="content-save"
          onClick={() => setSaved(favorites.toggle({ slug, title: c.title, type: c.type }))}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-border bg-white px-5 text-sm text-ink-700"
        >
          {saved ? <BookmarkCheck size={16} className="text-sage-600" /> : <Bookmark size={16} />} {saved ? "Salvo" : "Salvar"}
        </button>
        <button
          data-testid="content-share"
          onClick={share}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-border bg-white px-5 text-sm text-ink-700"
        >
          <Share2 size={16} /> Compartilhar
        </button>
      </div>

      <article>
        <Markdown testId="content-body">{c.body || c.summary}</Markdown>
      </article>

      {data.related?.length ? (
        <section data-testid="content-related">
          <h2 className="font-display text-lg font-medium text-ink-900">Relacionados</h2>
          <div className="mt-3 space-y-3">
            {data.related.map((r) => (
              <Link key={r.id} to={`/saude/conteudos/${r.slug}`} className="er-card block">
                <span className="font-display text-base font-medium text-ink-900">{r.title}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
      <EmergencyBar />
    </div>
  );
};

export const StaticPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    (async () => {
      try {
        const { data: payload } = await cachedGet(`/contents/${slug}`);
        setData(payload);
      } catch {
        setError("Página indisponível no momento.");
      }
    })();
  }, [slug]);

  if (error) return <StateBlock testId="page-error" title="Página indisponível" description={error} />;
  if (!data) return <Skeleton lines={4} testId="page-skeleton" />;

  return (
    <div data-testid={`saude-page-${slug}`} className="space-y-5">
      <h1 className="font-display text-2xl font-medium tracking-tight text-ink-900">{data.content.title}</h1>
      <Markdown testId={`page-body-${slug}`}>{data.content.body || data.content.summary}</Markdown>
      <EmergencyBar />
    </div>
  );
};

export const SavedContents = () => {
  const [items, setItems] = useState(favorites.list());
  return (
    <div data-testid="saude-saved-page" className="space-y-5">
      <h1 className="font-display text-2xl font-medium tracking-tight text-ink-900">Conteúdos salvos</h1>
      <p className="text-sm text-ink-500">Seus salvos ficam somente neste dispositivo, sem necessidade de conta.</p>
      {items.length === 0 ? (
        <StateBlock testId="saved-empty" title="Nada salvo ainda" description="Toque em Salvar em qualquer conteúdo para guardá-lo aqui." />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.slug} className="er-card flex items-center justify-between gap-3" data-testid={`saved-item-${item.slug}`}>
              <Link to={`/saude/conteudos/${item.slug}`} className="font-display text-base font-medium text-ink-900">
                {item.title}
              </Link>
              <button
                data-testid={`saved-remove-${item.slug}`}
                onClick={() => {
                  favorites.toggle(item);
                  setItems(favorites.list());
                }}
                className="text-xs text-danger"
              >
                Remover
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const RecentContents = () => {
  const [items, setItems] = useState(history.list());
  return (
    <div data-testid="saude-recent-page" className="space-y-5">
      <h1 className="font-display text-2xl font-medium tracking-tight text-ink-900">Vistos recentemente</h1>
      {items.length === 0 ? (
        <StateBlock testId="recent-empty" title="Nenhum conteúdo visto ainda" />
      ) : (
        <>
          <div className="space-y-3">
            {items.map((item) => (
              <Link
                key={item.slug}
                to={`/saude/conteudos/${item.slug}`}
                data-testid={`recent-item-${item.slug}`}
                className="er-card block font-display text-base font-medium text-ink-900"
              >
                {item.title}
              </Link>
            ))}
          </div>
          <button
            data-testid="recent-clear"
            onClick={() => {
              history.clear();
              setItems([]);
            }}
            className="text-xs text-danger"
          >
            Limpar histórico deste dispositivo
          </button>
        </>
      )}
    </div>
  );
};
